import React, { useEffect, useState } from "react";
import { fetchUserProfile, fetchUserTournaments, toggleUserTournamentEnabled, updateProfile } from "../api/api.js";
import { useAuth } from "../context/AuthContext";
import countries from "../data/countries";
import {
    getTournamentDisplayName
} from "../utils/tournamentUtils";

const Profile = () => {
    const { logout } = useAuth();
    const [profile, setProfile] = useState(null);
    const [superAdmin, setSuperAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState("profile");
    const [name, setName] = useState("");
    const [showName, setShowName] = useState(false);
    const [country, setCountry] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [tournaments, setTournaments] = useState(null);
    const [successStatus, setSuccessStatus] = useState({});

    useEffect(() => {
        if (mode === "profile") {
            loadProfile();
        } else if (mode === "tournaments") {
            loadTournaments();
        } else if (mode === "super-admin") {
            window.location.href = "/super-admin";
        }
    }, [mode]);

    const loadProfile = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchUserProfile();
            setProfile(response.data.profile);
            setName(response.data.profile.name);
            setShowName(response.data.profile.show_name);
            setCountry(response.data.profile.country);
            setSuperAdmin(response.data.me && response.data.me === 'super-admin');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadTournaments = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchUserTournaments();
            setTournaments(response.data.tournaments);
            setSuperAdmin(response.data.me && response.data.me === 'super-admin');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const setSuccess = (id, value) => {
        setSuccessStatus(prev => ({ ...prev, [id]: value }));
    };

    const toggleTournamentStatus = async (tournamentId) => {
        try {
            const response = await toggleUserTournamentEnabled(tournamentId)
            if (response.status === 'failed') {
                setSuccess(tournamentId, "error");
                return
            }

            setTournaments(prev =>
                prev.map(t =>
                    t.id === tournamentId ? { ...t, enabled: !t.enabled } : t
                )
            );
            localStorage.removeItem("predict-filter");

            setSuccess(tournamentId, "success")
        } catch (error) {
            setSuccess(tournamentId, "error")
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const response = await updateProfile(name, showName ? 1 : 0, country)

        if (response.status === 'success') {
            window.location.reload();
        } else {
            setError(response.message);
            setSubmitting(false);
        }
    };

    const renderProfile = () => {
        if (!profile) return <p>No profile found</p>;

        return (
            <>
                <div className="user-info-containter">
                    <div className="flag-name-container">
                        <img
                            src={profile.country_img}
                            alt={profile.country}
                            title={profile.country}
                            className="user-country-flag"
                        />
                        <div className="name">
                            <span className="username"> {profile.username} </span>
                            {profile.name}
                        </div>
                    </div>
                </div>
                <div>
                    <form className="profile-form" onSubmit={handleSubmit} >
                        <div className="form-group">
                            <label>Username</label>
                            <input type="text" value={profile.username} disabled />
                        </div>

                        <div className="form-group">
                            <label>Email</label>
                            <input type="email" value={profile.email} disabled />
                        </div>

                        <div className="form-group">
                            <label>Name</label>
                            <input type="text" value={name} onChange={(e) => setName(e.target.value)}/>
                        </div>

                        <div className="form-group checkbox-group">
                            <label className="checkbox-label">
                                <input type="checkbox" checked={showName} onChange={(e) => setShowName(e.target.checked)} />
                                Show Name
                                </label>
                            <span className="checkbox-info">
                            If enabled other users can see your set name
                            </span>
                        </div>

                        <div className="form-group">
                            <label>Country</label>
                            <select
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                            >
                                {countries.map((c, index) => (
                                    <option key={index} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>

                        <button type="submit" className="submit-button" disabled={submitting}>{submitting ? "Saving..." : "Save"}</button>
                    </form>
                </div>
            </>
        );
    };

    const renderTournaments = () => {
        if (!tournaments) return <p>No tournaments found</p>;

        return (
            <table className="update-table">
                <thead>
                    <tr>
                        <th>Tournament</th>
                        <th>Current Status</th>
                        <th>Action</th>
                        <th>Success</th>
                    </tr>
                </thead>
                <tbody>
                    {tournaments.map(t => (
                        <tr key={t.id}>
                            <td><b>{getTournamentDisplayName(t.name)}</b></td>
                            <td>{t.enabled ? "Enabled" : "Disabled"}</td>
                            <td>
                                <button onClick={() => toggleTournamentStatus(t.id)}>
                                    {t.enabled ? "Disable" : "Enable" }
                                </button>
                            </td>
                            <td>
                                {successStatus[t.id] === "success" && (
                                    <span style={{ color: "green", fontSize: "20px" }}>✔</span>
                                )}
                                {successStatus[t.id] === "error" && (
                                    <span style={{ color: "red", fontSize: "20px" }}>✖</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderLogout = () => {
        return (
            <button className="submit-button" onClick={logout}>Logout</button>
        )
    }

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error}</p>;

    return (
        <div className="content-container">

            <div className="filter-dropdown-container">
                <select
                    className="filter-dropdown points-dropdown"
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                >
                    <option value="profile">Profile</option>
                    <option value="tournaments">Tournaments</option>
                    { superAdmin && <option value="super-admin">Super Admin</option>}
                    <option value="logout">Logout</option>
                </select>
            </div>

            <div className={`user-points dark-main-container ${ mode !== "logout" ? "scroll-wrapper" : "" }`}>
                {mode === "profile" && renderProfile()}
                {mode === "tournaments" && renderTournaments()}
                {mode === "logout" && renderLogout()}
            </div>

        </div>
    );

}

export default Profile;